import React, { memo, useMemo, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Import our custom hooks
import { usePagination } from '../../hooks/common';

// Types
export interface OptimizedTableColumn<T> {
  id: keyof T;
  label: string;
  minWidth?: number;
  align?: 'left' | 'right' | 'center';
  format?: (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
}

export interface OptimizedTableProps<T> {
  data: T[];
  columns: OptimizedTableColumn<T>[];
  loading?: boolean;
  error?: string | null;
  totalCount?: number;
  onPageChange?: (page: number, rowsPerPage: number) => void;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  rowsPerPageOptions?: number[];
  initialRowsPerPage?: number;
}

// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '8px',
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.primary,
}));

const LoadingRow = memo(() => (
  <TableRow>
    <TableCell colSpan={100} align="center" sx={{ py: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    </TableCell>
  </TableRow>
));

const EmptyRow = memo<{ message: string; colSpan: number }>(({ message, colSpan }) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </TableCell>
  </TableRow>
));

const ErrorRow = memo<{ error: string; colSpan: number }>(({ error, colSpan }) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
      <Typography variant="body2" color="error">
        {error}
      </Typography>
    </TableCell>
  </TableRow>
));

// Memoized Table Row Component
const TableRowMemo = memo<{
  row: any;
  columns: OptimizedTableColumn<any>[];
  onRowClick?: (row: any) => void;
}>(({ row, columns, onRowClick }) => {
  const handleClick = useCallback(() => {
    onRowClick?.(row);
  }, [onRowClick, row]);

  return (
    <TableRow 
      hover 
      onClick={handleClick}
      sx={{ 
        cursor: onRowClick ? 'pointer' : 'default',
        '&:hover': onRowClick ? { backgroundColor: 'action.hover' } : {}
      }}
    >
      {columns.map((column) => {
        const value = row[column.id];
        const formattedValue = column.format ? column.format(value, row) : value;
        
        return (
          <TableCell
            key={String(column.id)}
            align={column.align}
            sx={{ minWidth: column.minWidth }}
          >
            {formattedValue}
          </TableCell>
        );
      })}
    </TableRow>
  );
});

// Main Optimized Table Component
const OptimizedTable = <T,>({
  data,
  columns,
  loading = false,
  error = null,
  totalCount = 0,
  onPageChange,
  onRowClick,
  emptyMessage = 'No data available',
  rowsPerPageOptions = [5, 10, 25, 50],
  initialRowsPerPage = 10,
}: OptimizedTableProps<T>) => {
  const {
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  } = usePagination({
    initialRowsPerPage,
  });

  // Memoized pagination handlers
  const handlePageChange = useCallback((event: unknown, newPage: number) => {
    handleChangePage(event, newPage);
    onPageChange?.(newPage, rowsPerPage);
  }, [handleChangePage, onPageChange, rowsPerPage]);

  const handleRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    handleChangeRowsPerPage(event);
    onPageChange?.(0, parseInt(event.target.value, 10));
  }, [handleChangeRowsPerPage, onPageChange]);

  // Memoized table content
  const tableContent = useMemo(() => {
    if (loading) {
      return <LoadingRow />;
    }

    if (error) {
      return <ErrorRow error={error} colSpan={columns.length} />;
    }

    if (data.length === 0) {
      return <EmptyRow message={emptyMessage} colSpan={columns.length} />;
    }

    return data.map((row, index) => (
      <TableRowMemo
        key={index}
        row={row}
        columns={columns}
        onRowClick={onRowClick}
      />
    ));
  }, [loading, error, data, columns, emptyMessage, onRowClick]);

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <StyledTableContainer>
        <Table stickyHeader>
          <StyledTableHead>
            <TableRow>
              {columns.map((column) => (
                <StyledTableCell
                  key={String(column.id)}
                  align={column.align}
                  sx={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </StyledTableCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {tableContent}
          </TableBody>
        </Table>
      </StyledTableContainer>
      
      {totalCount > 0 && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}
    </Paper>
  );
};

export default memo(OptimizedTable) as <T>(props: OptimizedTableProps<T>) => JSX.Element;
